import { ReactRenderer } from '@tiptap/react';
import { Extension } from '@tiptap/core';
import Suggestion from '@tiptap/suggestion';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import { VariableCommandMenu, VariableCommandMenuHandle } from '@/components/ui/variable-command-menu';
import React from 'react';

export interface VariableSuggestionOptions {
  onInsert?: (variableKey: string) => void;
  isEmail?: boolean;
}

export const VariableSuggestion = Extension.create<VariableSuggestionOptions>({
  name: 'variableSuggestion',

  addOptions() {
    return {
      onInsert: undefined,
      isEmail: true,
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        char: '/',
        startOfLine: false,
        allowSpaces: true,

        command: ({ editor, range, props }) => {
          // Delete the '/' character and any query text
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertVariablePill(props.variableKey)
            .run();
        },

        items: ({ query }) => {
          // We don't filter here - the CommandMenu component handles filtering
          return [{ query }];
        },

        render: () => {
          let component: ReactRenderer<VariableCommandMenuHandle, any> | null = null;
          let popup: TippyInstance[] | null = null;

          return {
            onStart: (props: any) => {
              component = new ReactRenderer(VariableCommandMenu, {
                props: {
                  query: props.query,
                  onSelect: (variableKey: string) => {
                    props.command({ variableKey });
                  },
                  isEmail: this.options.isEmail,
                  className: 'w-80 border border-border shadow-lg rounded-lg overflow-hidden',
                },
                editor: props.editor,
              });

              if (!props.clientRect) {
                return;
              }

              popup = tippy('body', {
                getReferenceClientRect: props.clientRect as () => DOMRect,
                appendTo: () => document.body,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: 'manual',
                placement: 'bottom-start',
                theme: 'variable-suggestion',
                maxWidth: 'none',
              });
            },

            onUpdate(props: any) {
              component?.updateProps({
                query: props.query,
                onSelect: (variableKey: string) => {
                  props.command({ variableKey });
                },
              });

              if (!props.clientRect) {
                return;
              }

              popup?.[0]?.setProps({
                getReferenceClientRect: props.clientRect as () => DOMRect,
              });
            },

            onKeyDown(props: any) {
              if (props.event.key === 'Escape') {
                popup?.[0]?.hide();
                return true;
              }

              // Forward keyboard events to the Command component
              if (component?.ref?.onKeyDown) {
                return component.ref.onKeyDown(props.event);
              }

              return false;
            },

            onExit() {
              popup?.[0]?.destroy();
              component?.destroy();
            },
          };
        },
      }),
    ];
  },
});
